import { useCallback, useMemo } from 'react'
import { Check, ShieldCheck, X } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import { fetchCreatives, moderateCreative, ADS_STR as S, type CreativeRow } from './adsApi'

/**
 * A20.05 — Модерация креативов. Approve/reject a submitted ad creative via
 * `POST /admin/ads/creatives/:id/moderate` (dormant until it ships). Row actions
 * only show on `pending` rows. Placeholder permission USERS_VIEW.
 */
export function CreativesModerationScreen() {
  const { toast } = useToast()

  const columns = useMemo<AdminColumn<CreativeRow>[]>(
    () => [
      { key: 'campaign', header: S.title, type: 'text' },
      { key: 'advertiser', header: S.advertiser, type: 'text' },
      { key: 'kind', header: S.kind, type: 'text' },
      { key: 'status', header: S.status, type: 'text' },
      { key: 'submitted_at', header: S.submitted, type: 'text' },
    ],
    [],
  )

  const moderate = useCallback(
    async (row: CreativeRow, decision: 'approve' | 'reject') => {
      try {
        await moderateCreative(row.id, decision)
        toast(decision === 'approve' ? S.approved : S.rejected, { tone: 'success' })
      } catch {
        toast(S.moderateFailed, { tone: 'error' })
      }
    },
    [toast],
  )

  const rowActions = useMemo<RowAction<CreativeRow>[]>(
    () => [
      {
        id: 'approve',
        label: S.approve,
        icon: <Check size={14} />,
        onSelect: (r) => moderate(r, 'approve'),
        visible: (r) => r.status === 'pending',
      },
      {
        id: 'reject',
        label: S.reject,
        icon: <X size={14} />,
        danger: true,
        onSelect: (r) => moderate(r, 'reject'),
        visible: (r) => r.status === 'pending',
      },
    ],
    [moderate],
  )

  const fetcher = useCallback(fetchCreatives, [])
  return (
    <>
      <PageHeader title={S.creatives} screenId="A20.05" />
      <DataTable<CreativeRow>
        screenId="A20.05"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        emptyState={{ icon: <ShieldCheck size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
